import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/20/solid";
import {
  buildGroupedTemplateOptions,
  getTemplateColors,
} from "../utils/templateToServiceCategory";
import SuggestedChip from "./SuggestedChip";

/**
 * Templates dropdown grouped by V2 service category, with a "Custom" group
 * at the bottom for unmapped/non-Diligent templates.
 *
 * JSX, Tailwind classes, group-header rendering and item rendering are
 * ported verbatim from Innovation Lab prototype `RFIGridFilters.jsx`
 * (jackson-master) lines 663-705. The grouping algorithm and template→SC
 * map live in `utils/templateToServiceCategory.js`.
 *
 * Controlled component:
 *   - options: Array<{ value, label, global?, owner_id? }>
 *   - selected: Array<same shape>
 *   - onChange: (next) => void
 *   - rfiList: Array passed through to getV2Meta for SC→group lookup
 *   - lockedValue: optional value that cannot be removed (Core RFI pinning)
 *   - placeholder: optional placeholder text
 *   - suggestedValues: Set|Array of template option values that show "Suggested"
 *     (1:1 with VendorListedServicesSuggestions / findTemplateForServiceCategory)
 */
export default function TemplateGroupedDropdown({
  options = [],
  selected = [],
  onChange,
  rfiList = [],
  lockedValue = null,
  placeholder = "Select Templates",
  suggestedValues = null,
}) {
  const suggestedValueSet = useMemo(() => {
    if (!suggestedValues) return null;
    return suggestedValues instanceof Set
      ? suggestedValues
      : new Set(suggestedValues);
  }, [suggestedValues]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  // Group templates by V2 service category (memoized on options + rfiList)
  const groupedOptions = useMemo(
    () => buildGroupedTemplateOptions(options, rfiList),
    [options, rfiList]
  );

  // Apply search filter — keep group headers whose group has any match
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return groupedOptions;
    const q = searchQuery.toLowerCase();
    const matchingGroups = new Set();
    groupedOptions.forEach((opt) => {
      if (!opt.isGroupHeader && (opt.label || "").toLowerCase().includes(q)) {
        matchingGroups.add(opt.groupName);
      }
    });
    return groupedOptions.filter((opt) => {
      if (opt.isGroupHeader) return matchingGroups.has(opt.groupName);
      return (opt.label || "").toLowerCase().includes(q);
    });
  }, [groupedOptions, searchQuery]);

  // Dedupe by label so duplicate-named entries don't produce duplicate badges
  const dedupeByLabel = (arr) => {
    const seen = new Set();
    const result = [];
    for (const item of arr || []) {
      const key = (item?.label ?? item?.value ?? "").toString().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  };

  const emit = (next) => {
    if (onChange) onChange(dedupeByLabel(next));
  };

  const isSelected = (value) => selected.some((s) => s.value === value);

  const handleToggle = (option) => {
    if (option.isGroupHeader) {
      // Toggle entire group: select all if any unselected, otherwise unselect all
      const memberValues = option.groupMembers || [];
      const memberOptions = groupedOptions.filter(
        (o) => !o.isGroupHeader && memberValues.includes(o.value)
      );
      const allSelected = memberOptions.every((o) => isSelected(o.value));
      if (allSelected) {
        // Don't unselect the locked value
        emit(
          selected.filter(
            (s) => !memberValues.includes(s.value) || s.value === lockedValue
          )
        );
      } else {
        const existing = new Set(selected.map((s) => s.value));
        const toAdd = memberOptions.filter((o) => !existing.has(o.value));
        // Strip the dotColorClass/groupName meta — keep the original
        // option shape so onChange receives clean form values
        const cleanToAdd = toAdd.map((o) =>
          stripMeta(options.find((src) => src.value === o.value) || o)
        );
        emit([...selected, ...cleanToAdd]);
      }
      return;
    }
    if (isSelected(option.value)) {
      if (option.value === lockedValue) return;
      emit(selected.filter((s) => s.value !== option.value));
    } else {
      // Use the source option (without dotColorClass/groupName meta)
      const src = options.find((o) => o.value === option.value) || option;
      emit([...selected, stripMeta(src)]);
    }
  };

  const stripMeta = (opt) => {
    const {
      dotColorClass,
      colors,
      groupName,
      isGroupHeader,
      groupMembers,
      ...rest
    } = opt || {};
    return rest;
  };

  const removeTag = (value) => {
    if (value === lockedValue) return;
    emit(selected.filter((s) => s.value !== value));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input bar with selected badges */}
      <div
        className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[24px]">
          {selected.length > 0 ? (
            dedupeByLabel(selected)
              .filter((tag) => tag && (tag.value != null || tag.label != null))
              .map((tag) => {
                const tagValue =
                  typeof tag.value === "string" || typeof tag.value === "number"
                    ? tag.value
                    : "";
                const tagLabel =
                  typeof tag.label === "string"
                    ? tag.label
                    : typeof tag.value === "string"
                      ? tag.value
                      : "";
                const isLocked = tagValue === lockedValue;
                const colors = getTemplateColors(tagLabel, rfiList);
                return (
                  <span
                    key={tagValue}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      "--tw-ring-color": colors.border + "33",
                    }}
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors.text }}
                    />
                    {tagLabel}
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTag(tagValue);
                        }}
                        className="ml-0.5 inline-flex items-center hover:opacity-70"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                );
              })
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          {/* Search input */}
          <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:outline-none"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Grouped options list */}
          <div
            className="max-h-[26rem] overflow-y-auto py-1 px-3"
            style={{ paddingBottom: "1rem" }}
          >
            <ul className="space-y-0">
              {filteredOptions.map((option, idx) => {
                if (option.isGroupHeader) {
                  const memberValues = option.groupMembers || [];
                  const selectedInGroup = memberValues.filter((v) =>
                    selected.some((s) => s.value === v)
                  );
                  const allSelected =
                    memberValues.length > 0 &&
                    selectedInGroup.length === memberValues.length;
                  const someSelected =
                    selectedInGroup.length > 0 && !allSelected;
                  return (
                    <li
                      key={`grp-${idx}`}
                      className="pt-3 pb-1 first:pt-0 select-none"
                    >
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-wider cursor-pointer transition-colors ${
                          allSelected || someSelected
                            ? "text-indigo-600"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(option);
                        }}
                      >
                        {option.label}
                      </span>
                    </li>
                  );
                }
                const checked = isSelected(option.value);
                return (
                  <li
                    key={`tpl-${idx}`}
                    className={`relative select-none py-1.5 pl-3 pr-9 cursor-pointer rounded-md ${
                      checked ? "bg-indigo-50" : "hover:bg-indigo-50"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option);
                    }}
                    title={option.label}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: option.colors?.text || "#9CA3AF",
                        }}
                      />
                      <span
                        className={`inline-flex items-center min-w-0 text-sm leading-5 ${
                          checked
                            ? "font-semibold text-gray-900"
                            : "font-normal text-gray-700"
                        }`}
                      >
                        <span className="truncate">{option.label}</span>
                        {suggestedValueSet?.has(option.value) && (
                          <SuggestedChip />
                        )}
                      </span>
                    </div>
                    {checked && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg
                          className="h-5 w-5 text-indigo-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
